import { RootState } from '../../../../app/store';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../../app/store/utils/baseQueryWithReauth';

const API_HOST = process.env.REACT_APP_API_HOST;

export interface Subscription {
  _id?: string;
  name?: string;
  description?: string;
  price?: number;
  features?: string[];
  [key: string]: any;
}

export interface SubscriptionCreateRequest {
  name: string;
  description?: string;
  price?: number;
  features?: string[];
  [key: string]: any;
}

export interface SubscriptionUpdateRequest {
  name?: string;
  description?: string;
  price?: number;
  features?: string[];
  [key: string]: any;
}

export interface SubscribeRequest {
  subscriptionId: string;
  [key: string]: any;
}

/**
 * Subscription API (RTK Query)
 */
export const subscriptionApi = createApi({
  reducerPath: 'subscriptionApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Subscription'],
  endpoints: (build) => ({
    getSubscriptions: build.query<{ data: Subscription[]; total: number }, { page?: number; limit?: number; sortBy?: string; sortOrder?: string }>({
      query: (params) => {
        const query = new URLSearchParams();
        if (params.page) query.append('page', params.page.toString());
        if (params.limit) query.append('limit', params.limit.toString());
        if (params.sortBy) query.append('sortBy', params.sortBy);
        if (params.sortOrder) query.append('sortOrder', params.sortOrder);

        return {
          url: `subscription/list?${query.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Subscription'],
    }),

    getSubscriptionById: build.query<Subscription, string>({
      query: (id) => ({
        url: `management/subscription/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Subscription', id }],
    }),

    createSubscription: build.mutation<Subscription, SubscriptionCreateRequest>({
      query: (data) => ({
        url: 'management/subscription',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Subscription'],
    }),

    updateSubscription: build.mutation<Subscription, { id: string; data: SubscriptionUpdateRequest }>({
      query: ({ id, data }) => ({
        url: `management/subscription/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Subscription', id }, 'Subscription'],
    }),

    deleteSubscription: build.mutation<void, string>({
      query: (id) => ({
        url: `subscription/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Subscription'],
    }),

    getOwnSubscription: build.query<Subscription, void>({
      query: () => ({
        url: 'subscription/own',
        method: 'GET',
      }),
      providesTags: ['Subscription'],
    }),

    getOwnSubscriptionList: build.query<{ data: Subscription[]; total: number }, { page?: number; limit?: number; sortBy?: string; sortOrder?: string }>({
      query: (params) => {
        const query = new URLSearchParams();
        if (params.page) query.append('page', params.page.toString());
        if (params.limit) query.append('limit', params.limit.toString());
        if (params.sortBy) query.append('sortBy', params.sortBy);
        if (params.sortOrder) query.append('sortOrder', params.sortOrder);

        return {
          url: `subscription/own/list?${query.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Subscription'],
    }),

    subscribe: build.mutation<Subscription, SubscribeRequest>({
      query: (data) => ({
        url: 'subscription/subscribe',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Subscription'],
    }),

    getSubscriptionFeatures: build.query<any[], void>({
      query: () => ({
        url: 'management/subscription/feature',
        method: 'GET',
      }),
      providesTags: ['Subscription'],
    }),
  }),
});

export const {
  useGetSubscriptionsQuery,
  useGetSubscriptionByIdQuery,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useGetOwnSubscriptionQuery,
  useGetOwnSubscriptionListQuery,
  useSubscribeMutation,
  useGetSubscriptionFeaturesQuery,
} = subscriptionApi;

